import { Button } from '@mantine/core';
import { useMutation, useQuery } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { ApiClient } from '../../../../api/ApiClient';
import { v2 } from '../../../../api/ApiInterfaceV2';
import { useIsMounted, useObserverReturningStore, waitFor } from '../../../../CodesThatsGottaGoSomewhereElse';
import { FlexScroller } from '../../../../ui/Scrollers';
import { useResolve } from '../../../Container';
import { EmptyBlock } from '../../../dashboard/elements/EmptyBlock';
import { Flexer, TextArea, useSubmitWithCtrlEnter } from '../../../dashboard/elements/LayoutElements';
import { AdfViewer } from '../../../external/atlassian/adf/AdfViewer';
import { JiraIssueSidebarActions } from './JiraIssueSidebarActions';
import { useJiraIssueRecord } from './JiraIssueSidebarHooks';

// SO just to explain the nonse in here.  JiraIssueData.description is a v3 Atlassian API ADF document.  We don't have
// a rich ADF editor yet, so when editing, flip to v2 API Atlassian Markdown version, and then back to v3 ADF when done.

const useJiraIssueDescriptionQuery = (jiraIssueId: string) => {
    const api = useResolve(ApiClient);
    return useQuery({
        queryKey: ['jira.issues.description', jiraIssueId],
        queryFn: async () => {
            await waitFor(2000);
            return await api.exec(v2.jira.issues.description({ jiraIssueId }));
        },
    });
};

const useJiraIssueDescriptionMutation = (jiraIssueId: string) => {
    const api = useResolve(ApiClient);
    return useMutation({
        mutationFn: async (description: string) => {
            // Perform save
            await api.exec(v2.jira.issues.patch([{
                jiraIssueId,
                actions: [{
                    type: 'set',
                    params: {
                        prop: 'description',
                        value: description,
                    },
                }]
            }]));
            // Fetch updated version
            const { items } = await api.exec(v2.jira.issues.list({
                jql: `id = ${jiraIssueId}`,
            }));
            return items[0];
        },
    });
};

const DescriptionInput = styled(TextArea)`
    position: relative;
    height: 100%;
`;

const isDocEmpty = (doc?: any) => {
    if (!doc) return true;
    if (doc?.type !== 'doc') return true;
    if (doc?.content?.length === 0) return true;
    if (doc?.content?.length === 1 &&
        doc?.content[0]?.type === 'paragraph' &&
        (doc?.content[0]?.content?.length ?? 0) === 0) return true;
    return false;
};

export const JiraIssueDescriptionView = React.memo(({ jiraIssueId }: { jiraIssueId: string }) => {

    const mounted = useIsMounted();
    const amdDescQuery = useJiraIssueDescriptionQuery(jiraIssueId);
    const patchDesc = useJiraIssueDescriptionMutation(jiraIssueId);
    const record = useObserverReturningStore(useJiraIssueRecord(jiraIssueId));

    const empty = isDocEmpty(record.get('description'));
    const [editing, setEditing] = useState(false);

    const [adfDesc, setAdfDesc] = useState<any>(record.get('description'));
    const [amdDesc, setAmdDesc] = useState('');

    useEffect(() => {
        setAmdDesc(amdDescQuery.data?.description ?? '');
    }, [amdDescQuery.data?.description]);

    const onSave = () => {
        patchDesc.mutateAsync(amdDesc).then(issue => {
            if (!mounted.current) return;
            setEditing(false);
            setAdfDesc(issue.description);
            record.mutate(issue);
        });
    };

    const onKeyDown = useSubmitWithCtrlEnter(onSave);

    return <Flexer grow flex="column">
        <Flexer grow flex="column" padding=".5">
            {!editing && <>
                {!empty &&
                    <Flexer flex="column" grow padding=".5 0 0 .5">
                        <FlexScroller shadow>
                            <AdfViewer jiraIssueId={jiraIssueId} doc={adfDesc} />
                        </FlexScroller>
                    </Flexer>
                }
                {empty &&
                    <EmptyBlock grow minHeight="10" title="This issue has no description yet...">
                        <Button variant="filled" onClick={() => setEditing(true)}>
                            Add Description
                        </Button>
                    </EmptyBlock>
                }
            </>}
            {!!editing &&
                <DescriptionInput
                    autoFocus
                    disabled={amdDescQuery.isLoading}
                    value={amdDescQuery.isLoading ? 'Loading...' : amdDesc}
                    onChange={e => setAmdDesc(e.target.value)}
                    onBlur={onSave}
                    onKeyDown={onKeyDown}
                />
            }
        </Flexer>
        <JiraIssueSidebarActions jiraIssueId={jiraIssueId}>
            {editing && <>
                <Button
                    loading={patchDesc.isLoading}
                    variant="filled"
                    onClick={onSave}>
                    Save
                </Button>
                <Button
                    disabled={patchDesc.isLoading}
                    variant="default"
                    onClick={() => setEditing(false)}>
                    Cancel
                </Button>
            </>}
            {!editing && <Button variant="default" onClick={() => setEditing(true)}>Edit</Button>}
        </JiraIssueSidebarActions>
    </Flexer>;
});
